import React, { useState, useContext, useEffect, useRef } from 'react';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import classNames from 'classnames';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { motion, AnimatePresence } from 'framer-motion';
import ButtonSecondaryEllipse from '../ButtonSecondaryEllipse';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
// import PropTypes from 'prop-types';
import styles from './FooterContact.module.scss';
import { trackContactInteraction } from '../../utils/dataLayer';
import { ContactContext, GradientContext, BackgroundContext } from '../../containers/BasePage';
import Link from 'next/link';
import useWindowWidthTitles from '../../hooks/useWindowWidthTitles';
import AccordionList from '../AccordionList/AccordionList';

const FooterContact = ({
    titleTop,
    titleBottom,
    preamble,
    email,
    phone,
    teamMembers,
    goToForm,
    showTeamMembers,
    isDesktop,
    accordionTitle,
    accordionItems = [],
}) => {
    const hasScrolledIntoView = useRef(false);
    const [inViewRef, isVisible] = useIntersectionObserver(undefined, undefined, true);
    const [inViewRefCta, isVisibleCta] = useIntersectionObserver(undefined, undefined, true);
    const [bgRef, isVisibleBg, position] = useIntersectionObserver(undefined, undefined, false);
    const [isHovered, setIsHovered] = useState(false);

    const background = useContext(BackgroundContext);
    const { toggleWhiteBackground, setHasSetBg, whiteBackground } = background;
    const gradient = useContext(GradientContext);

    const width = useWindowWidthTitles(isDesktop);

    const isMobile = width < 768;

    const classes = classNames(
        styles['Root'],
        {
            [styles['Visible']]: isVisible,
            [styles['White']]: whiteBackground,
        },
        styles[gradient]
    );
    const { t } = useTranslation();
    const contact = useContext(ContactContext);

    useEffect(() => {
        if (isVisibleBg) {
            toggleWhiteBackground(false);
            setHasSetBg(true);
            hasScrolledIntoView.current = true;
        } else if (!isVisibleBg && hasScrolledIntoView.current && position === 'BELOW') {
            setHasSetBg(true);
            toggleWhiteBackground(true);
        }
    }, [isVisibleBg]);

    const hasTeamMembers = !!teamMembers?.items?.length;

    return (
        <section className={classes} ref={bgRef} id="footer-contact">
            <div className={`${styles['Container']} grid`} ref={inViewRef}>
                {titleTop && (
                    <div className={styles['Tagline']}>
                        Contact us
                        {!whiteBackground && hasScrolledIntoView.current && (
                            <i className={classNames(styles['Gradient'], styles['TopLeft'], styles[gradient])} />
                        )}
                    </div>
                )}
            </div>
            <div className={styles['Rel']}>
                {isMobile ? (
                    <h2 className={`${styles['Title']} ${styles['TitleMobile']} grid`}>
                        <span className={styles['TitleContainer']}>
                            <span className={styles['TitleTop']}>{titleTop}</span>
                            <span className={styles['TitleBottom']}> {titleBottom}</span>
                        </span>
                    </h2>
                ) : (
                    <h2 className={`${styles['Title']} ${styles['TitleDesktop']} grid`}>
                        <span className={styles['TitleTop']}>{titleTop}</span>
                        <span className={styles['TitleBottom']}> {titleBottom}</span>
                    </h2>
                )}

                <div className={'grid'}>
                    <div className={styles['Preamble']}>{preamble}</div>
                </div>

                <div className={'grid'}>
                    <div className={styles['AccordionContainer']}>
                        <AccordionList title={accordionTitle} items={accordionItems} fromPage="offerPage" />
                    </div>
                </div>

                {goToForm && (
                    <div
                        ref={inViewRefCta}
                        className={classNames(styles['CtaWrap'], 'grid', {
                            [styles['CtaVisible']]: isVisibleCta,
                            [styles['CtaWithTeamMembers']]: showTeamMembers,
                        })}>
                        <div className={`${styles['CtaBorder']}`}>
                            <div
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                className={`${styles['Cta']}`}
                                ref={inViewRefCta}>
                                <div className={styles['CtaContent']}>
                                    <h3 className={styles['CtaText']}>Send us a request</h3>
                                    {!!contact?.url && (
                                        <Link href={contact?.url} aria-hidden={true} className={styles['HiddenLink']}>
                                            Go to contact form
                                        </Link>
                                    )}

                                    <div className={styles['ActivityDuration']}>
                                        <Image
                                            className={styles['ActivityIcon']}
                                            width={22}
                                            height={22}
                                            src={`/img/clock--white.svg`}
                                            alt=""
                                        />

                                        {t('Generic.activityDuration')}
                                    </div>
                                </div>
                                <div className={styles['CtaBtn']}>
                                    <ButtonSecondaryEllipse
                                        asLink
                                        modifiers={['InlineFlex']}
                                        href={contact?.url}
                                        icon="icon-arrow-right-white"
                                        active={isHovered}>
                                        <span className="sr-only">Go to contact form</span>
                                    </ButtonSecondaryEllipse>
                                </div>
                            </div>
                        </div>
                        {showTeamMembers && (
                            <>
                                <div
                                    className={classNames(styles['BoxWrap'], {
                                        [styles['BoxMargin']]: !hasTeamMembers,
                                    })}>
                                    {!whiteBackground && hasScrolledIntoView.current && (
                                        <i
                                            className={classNames(
                                                styles['Gradient'],
                                                styles['MiddleRight'],
                                                styles[gradient]
                                            )}
                                        />
                                    )}
                                    {phone?.phoneNumber && <PhoneBox {...phone} />}
                                </div>
                                {hasTeamMembers && (
                                    <div
                                        className={classNames(styles['BoxWrap'], {
                                            [styles['BoxMargin']]: hasTeamMembers,
                                        })}>
                                        <TeamMembers {...teamMembers} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {!goToForm && (
                    <div className={'grid'}>
                        <div
                            className={classNames(styles['BoxWrap'], {
                                [styles['BoxMargin']]: !hasTeamMembers,
                            })}>
                            {!whiteBackground && hasScrolledIntoView.current && (
                                <i
                                    className={classNames(styles['Gradient'], styles['MiddleRight'], styles[gradient])}
                                />
                            )}
                            {email?.email && <EmailBox {...email} />}
                            {phone?.phoneNumber && <PhoneBox {...phone} />}
                        </div>
                        {hasTeamMembers && (
                            <div
                                className={classNames(styles['BoxWrap'], {
                                    [styles['BoxMargin']]: hasTeamMembers,
                                })}>
                                <TeamMembers {...teamMembers} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

const EmailBox = ({ email, text }) => {
    const [isOpened, setIsOpened] = React.useState(false);

    return (
        <div className={styles['EmailWrap']}>
            <div className={styles['EmailBorder']}>
                <div className={styles['EmailContainer']}>
                    <div className={classNames(styles['EmailButton'], { [styles['Opened']]: isOpened })}>
                        <h3 className={styles['BoxHeading']}>
                            <button
                                className={styles['EmailButton']}
                                aria-expanded={isOpened}
                                aria-controls="email-content"
                                onClick={() => {
                                    trackContactInteraction('email');
                                    setIsOpened(!isOpened);
                                }}>
                                <span className={styles['ButtonText']}>Send us an email</span>
                                <span className={styles['SmallButtonText']}>Show e-mail</span>
                                <span className={styles['EmailButtonIcon']}>
                                    <i className={styles['Bg']} />
                                    <i
                                        className={classNames(styles['ExpandButton'], {
                                            [styles['Open']]: isOpened,
                                        })}
                                    />
                                </span>
                            </button>
                        </h3>
                    </div>
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        id="email-content"
                        variants={{
                            open: { height: 'auto', opacity: 1 },
                            collapsed: {
                                opacity: 0,
                                height: 0,
                                transition: {
                                    height: {
                                        delay: 0.3,
                                    },
                                },
                            },
                        }}
                        transition={{
                            duration: 0.3,
                            opacity: {
                                duration: 0.2,
                                delay: 0.3,
                            },
                        }}>
                        {isOpened && (
                            <motion.div
                                className={classNames(styles['EmailContent'], {
                                    [styles['Opened']]: isOpened,
                                })}
                                variants={{
                                    open: { height: 'auto', opacity: 1 },
                                    collapsed: {
                                        opacity: 0,
                                        height: 0,
                                        transition: {
                                            height: {
                                                delay: 0.3,
                                            },
                                        },
                                    },
                                }}
                                transition={{
                                    duration: 0.3,
                                    opacity: {
                                        duration: 0.2,
                                        delay: 0.3,
                                    },
                                }}>
                                <div className={styles['Email']}>
                                    <a href={`mailto:${email}`}>{email}</a>
                                </div>
                                <div className={styles['Text']}>{text}</div>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

const PhoneBox = ({ phoneNumber, text }) => {
    const [isOpened, setIsOpened] = React.useState(false);

    return (
        <div className={styles['PhoneWrap']}>
            <div className={'black-glass'}>
                <div className={styles['PhoneContainer']}>
                    <div className={classNames(styles['PhoneButton'], { [styles['Opened']]: isOpened })}>
                        <h3 className={styles['BoxHeading']}>
                            <button
                                className={styles['PhoneButton']}
                                aria-expanded={isOpened}
                                aria-controls="phone-content"
                                onClick={() => {
                                    trackContactInteraction('phone');
                                    setIsOpened(!isOpened);
                                }}>
                                <span className={styles['ButtonText']}>Call the team</span>
                                <span className={styles['SmallButtonText']}>Show telephone number</span>
                                <span className={styles['PhoneButtonIcon']}>
                                    <i className={styles['Bg']} />
                                    <i
                                        className={classNames(styles['ExpandButton'], {
                                            [styles['Open']]: isOpened,
                                        })}
                                    />
                                </span>
                            </button>
                        </h3>
                    </div>
                    <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        id="phone-content"
                        variants={{
                            open: { height: 'auto', opacity: 1 },
                            collapsed: {
                                opacity: 0,
                                height: 0,
                                transition: {
                                    height: {
                                        delay: 0.3,
                                    },
                                },
                            },
                        }}
                        transition={{
                            duration: 0.3,
                            opacity: {
                                duration: 0.2,
                                delay: 0.3,
                            },
                        }}>
                        {isOpened && (
                            <motion.div
                                className={classNames(styles['PhoneContent'], {
                                    [styles['Opened']]: isOpened,
                                })}
                                variants={{
                                    open: { height: 'auto', opacity: 1 },
                                    collapsed: {
                                        opacity: 0,
                                        height: 0,
                                        transition: {
                                            height: {
                                                delay: 0.3,
                                            },
                                        },
                                    },
                                }}
                                transition={{
                                    duration: 0.3,
                                    opacity: {
                                        duration: 0.2,
                                        delay: 0.3,
                                    },
                                }}>
                                <div className={styles['Phone']}>
                                    <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                                </div>
                                <div className={styles['Text']} dangerouslySetInnerHTML={{ __html: text }} />
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

const TeamMembers = ({ items }) => {
    const [isOpened, setIsOpened] = React.useState(false);

    return (
        <div className={styles['TeamMembers']}>
            <h3 className={styles['TeamHeading']}>
                <button
                    className={styles['TeamButton']}
                    aria-expanded={isOpened}
                    aria-controls="team"
                    onClick={() => {
                        trackContactInteraction('team');
                        setIsOpened(!isOpened);
                    }}>
                    <span className={styles['TeamButtonIcon']}>
                        <i className={styles['Bg']} />
                        <i
                            className={classNames(styles['ExpandButton'], styles['Chevron'], {
                                [styles['Open']]: isOpened,
                            })}
                        />
                    </span>
                    <span className={styles['TeamButtonText']}>The team&apos;s contact details</span>
                </button>
            </h3>
            <motion.div
                key="content"
                initial="collapsed"
                animate="open"
                exit="collapsed"
                id="team"
                variants={{
                    open: { opacity: 1 },
                    collapsed: {
                        opacity: 0,
                    },
                }}
                transition={{
                    duration: 0.3,
                }}>
                <motion.div
                    className={classNames(styles['TeamContent'], {
                        [styles['Opened']]: isOpened,
                    })}
                    variants={{
                        open: { opacity: 1 },
                        collapsed: {
                            opacity: 0,
                        },
                    }}
                    transition={{
                        duration: 0.3,
                    }}>
                    {isOpened && (
                        <>
                            {items.map((item, index) => (
                                <TeamMember key={index} {...item} />
                            ))}
                        </>
                    )}
                </motion.div>
            </motion.div>
        </div>
    );
};

const TeamMember = ({ name, title, email, phoneNumber }) => {
    return (
        <div className={styles['TeamMember']}>
            <h3 className={styles['TeamMemberName']}>{name}</h3>
            <div className={styles['TeamMemberTitle']}>{title}</div>
            <div className={styles['Phone']}>
                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
            </div>
            <div className={styles['Email']}>
                <a href={`mailto:${email}`}>{email}</a>
            </div>
        </div>
    );
};

FooterContact.propTypes = {};

export default FooterContact;
